<template>
  <v-btn
    class="button"
    prepend-icon="msf:group"
    active-color="primary"
    :active="opened"
    stacked
    density="compact"
    size="small"
    variant="flat"
    :rounded="4"
    @click="toggle"
    v-if="user.isAssistant"
  >
    {{ $t('meeting.participants.title') }}
  </v-btn>
  <v-menu
    :close-on-content-click="false"
    :offset="[10, 0]"
    activator="#leave-button"
    v-model="opened"
    location="bottom right"
  >
    <v-sheet class="participant-list" :min-width="320">
      <v-sheet class="participant-item" v-for="member in participants">
        <a-icon :name="getNetworkIcon(member.profile?.qualityNetwork)" :width="16" :height="16" />
        <v-icon size="small" :icon="iconMap[member.party]" />
        <span class="participant-name">{{ member.name }}</span>
        <template v-if="member.party !== 'assistant'">
          <v-icon
            v-if="member.profile?.microphone !== 'hidden'"
            size="small"
            :icon="`msf:mic${member.profile?.microphone === 'on' ? '' : '_off'}`"
            @click="muteParticipant(member.id, 'audio')"
          />
          <v-icon
            v-if="member.profile?.camera !== 'hidden'"
            size="small"
            :icon="`msf:${member.profile?.camera === 'on' ? 'videocam' : 'videocam_off'}`"
            @click="muteParticipant(member.id, 'video')"
          />
          <v-icon
            v-if="member.profile?.hasTorchCapability"
            size="small"
            :icon="`msf:flashlight_${member.profile?.torchStatus ? 'on' : 'off'}`"
            @click="muteParticipant(member.id, 'torch')"
          />
        </template>
        <span class="participant-type">{{ member.party }}</span>
      </v-sheet>
      <v-sheet v-if="user.isAssistant" class="participant-item add-member" @click="addMemberHandler">
        <v-icon icon="add"></v-icon>
        {{ $t('meeting.participants.add') }}
      </v-sheet>
    </v-sheet>
  </v-menu>
</template>
<script setup>
import {useBroadcast} from '@/composables'
import {participant} from '@/messages'
import {useAppStore, useSessionStore, useUserStore} from '@/stores'
import {useStreamStore} from '@/stores/streamStore'
import {storeToRefs} from 'pinia'
import {computed, ref} from 'vue'

const networkIconMapping = {
  good: 'connection-good',
  average: 'connection-average',
  poor: 'connection-poor',
  offline: 'connection-poor',
}
const getNetworkIcon = quality => networkIconMapping[quality] ?? 'connection-average'
const iconMap = {
  client: 'msf:person',
  assistant: 'msf:headset_mic',
  observer: 'msf:visibility',
}
const app = useAppStore()
const session = useSessionStore()
const {participants: sessionParticipants} = storeToRefs(session)
const user = useUserStore()
const broadcast = useBroadcast()
const streamStore = useStreamStore()
const {micEnabled} = storeToRefs(streamStore)
const opened = ref(false)
const toggle = event => {
  opened.value = !opened.value
  event.stopPropagation()
}
const roleSort = ['client', 'assistant', 'observer']
const participants = computed(() =>
  sessionParticipants.value
    .filter(member => roleSort.includes(member.party))
    .sort((a, b) => roleSort.indexOf(a.party) - roleSort.indexOf(b.party))
    .map(member => {
      let name
      if ((member.fullName === 'Guest' || member.fullname === 'Guest') && member.contact) {
        name = member.contact
      } else {
        name = member.fullName ?? member.fullname
      }
      return {
        name: name,
        ...member,
      }
    })
)
function muteParticipant(id, deviceType) {
  if (user.isAssistant) {
    if (Number(user.id) === Number(id)) {
      if (deviceType === 'audio') {
        micEnabled.value = false
      }
    } else {
      broadcast(participant.muteMessage(id, deviceType))
    }
  }
}
function addMemberHandler() {
  const {conference: callId} = session
  const {
    configuration: {
      session: {detailUrl},
    },
  } = app
  const url = detailUrl.replace('{{callId}}', callId)
  window.open(url, '_blank')
}
</script>
<style lang="scss" scoped>
@import '@/scss/button-group';
</style>
<style lang="scss">
.participant-list {
  display: flex;
  flex-direction: column;
  padding: $spacing-s $spacing-m;
  border-radius: $radius-rounded-8;
  gap: 8px;

  .participant-item {
    display: flex;
    flex-direction: row;
    padding: $spacing-s $spacing-m;
    justify-content: center;
    align-items: center;
    gap: $spacing-xs;
    border-radius: $radius-base;
    background: $background-neutral-6;
    border: 1px solid $border-divider-base;
    font-size: 14px;

    .participant-name {
      flex: 1;
    }

    .participant-type {
      color: $font-gray-2;
    }

    &.add-member {
      cursor: pointer;
      justify-content: start;
      border: 1px dashed $border-divider-base;
      color: #007da8;
    }
  }
}
</style>
